import type { MediaItem } from '../store/settings';

// Built-in demo video (Aidan Kirkpatrick swimming – his own footage, used at his request),
// played when a screen has no photos of its own.
//
// Each screen gets the best version it can play, falling back down the list if a version
// fails to load or decode:
//   1. HEVC 10-bit HDR (HLG), 4K 60 fps – near the original; Apple TV / Safari, most
//      Android & Google TV, Fire TV 4K, recent smart TVs. (HLG also looks right on SDR screens.)
//   2. H.264 4K – 4K screens without HEVC.
//   3. H.264 Full HD – everything else.
// The high-quality files live at DEMO_MEDIA_URL (server setting, e.g. on kcl.cloud). Without
// it, the smaller copies bundled in /demo are used and there is no HDR version.

const DIMS = { width: 3840, height: 2160 } as const;

interface Variant {
  id: string;
  file: string;
  /** Only for screens with ~4K physical pixels. */
  uhd: boolean;
  /** MIME type + codecs this variant needs (checked with canPlayType). */
  type?: string;
  /** Only available from DEMO_MEDIA_URL, not bundled. */
  externalOnly?: boolean;
}

const VARIANTS: Variant[] = [
  { id: 'demo:swim-hdr', file: 'swim-2160-hdr.mp4', uhd: true, type: 'video/mp4; codecs="hvc1.2.4.L153.B0"', externalOnly: true },
  { id: 'demo:swim-2160', file: 'swim-2160.mp4', uhd: true },
  { id: 'demo:swim-1080', file: 'swim-1080.mp4', uhd: false },
];

function physicalLongSide(): number {
  if (typeof window === 'undefined') return 1920;
  const { width, height } = window.screen;
  return Math.max(width, height) * (window.devicePixelRatio || 1);
}

function canPlay(type: string): boolean {
  try {
    return document.createElement('video').canPlayType(type) !== '';
  } catch {
    return false;
  }
}

/**
 * The demo video to show, or null if every suitable version has failed on this screen.
 * `failed` holds ids of versions that didn't play here.
 */
export function demoItem(quality: string, baseUrl: string | null | undefined, failed: Set<string>): MediaItem | null {
  const base = baseUrl || '/demo';
  const uhdScreen = quality !== '1080' && physicalLongSide() >= 3000;
  const v = VARIANTS.find(
    (x) =>
      !failed.has(x.id) &&
      (!x.uhd || uhdScreen) &&
      (!x.externalOnly || Boolean(baseUrl)) &&
      (!x.type || canPlay(x.type)),
  );
  // Ambient footage: crop the edges rather than letterbox on screens that aren't exactly 16:9.
  return v ? { id: v.id, kind: 'video', title: 'KCL Canvas demo', url: `${base}/${v.file}`, ...DIMS, alwaysFill: true } : null;
}
